// Returns the conversion rate FROM currency TO base currency
import { FXRates } from '@blk/aw-api-clients';
import { Security } from '@blk/awt-aladdinportfoliobuilderserver';
import Decimal from 'decimal.js';
import { isNil } from 'lodash';
import { cleanFloatingPointNoise } from './NumberUtils';

// Security group and type constants
export const SEC_GROUP_FX = 'FX';
export const SEC_GROUP_OPTION = 'OPTION';
export const SEC_TYPE_CUROTC = 'CUROTC';

export const FX_RATES_DECIMAL_PLACES = 4;

/**
 * Determines if a security is an FX security (FX forward or FX option).
 * FX securities include:
 * - Securities with secGroup 'FX' (FX forwards)
 * - Securities with secGroup 'OPTION' and secType 'CUROTC' (FX options)
 */
export const isFxSecurity = (security: Security | null | undefined): boolean => {
  if (!security) {
    return false;
  }
  return (
    security.secGroup === SEC_GROUP_FX ||
    (security.secGroup === SEC_GROUP_OPTION && security.secType === SEC_TYPE_CUROTC)
  );
};

// Returns the conversion rate FROM currentCurrency TO targetCurrency
export const getFxConversionRate = (currentCurrency: string, targetCurrency: string, fxRates: FXRates): number => {
  if (!!currentCurrency && !!targetCurrency && currentCurrency === targetCurrency) {
    return 1;
  }

  const baseCurrencyRate = fxRates[targetCurrency];
  const newCurrencyRate = fxRates[currentCurrency];

  const missingCurrencies: string[] = [];
  if (!baseCurrencyRate) {
    missingCurrencies.push(!!targetCurrency ? targetCurrency : 'undefined (targetCurrency)');
  }
  if (!newCurrencyRate) {
    missingCurrencies.push(!!currentCurrency ? currentCurrency : 'undefined (currentCurrency)');
  }

  if (missingCurrencies.length) {
    throw Error(
      `Unable to get conversion rate. FX Rates are missing for the following currencies: [${missingCurrencies.join(', ')}]`
    );
  }

  return new Decimal(baseCurrencyRate).div(new Decimal(newCurrencyRate)).toNumber();
};

export const getFXSecurityStrikePrice = (security: Security): number => {
  if (!security.valuations || !security.derivative) {
    throw Error('Security is missing strike price or derivative data');
  }
  const {
    derivative: { contractCurrency, alternativeCurrency, underlying },
    valuations: { strikePrice },
  } = security;
  if (`${contractCurrency}${alternativeCurrency}` !== underlying) {
    return 1 / strikePrice; // invert strike price if security pair is in different order
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- existing any
  return strikePrice;
};

export const calculateAlternateNotionalFromContractNotional = (contractNotional: number, security: Security) =>
  contractNotional * -1 * getFXSecurityStrikePrice(security);

export const calculateContractNotionalFromAlternateNotional = (alternateNotional: number, security: Security) =>
  (alternateNotional * -1) / getFXSecurityStrikePrice(security);

export const getPositionSizerTypeForSecurity = (security: Security): 'fxForward' | 'fxOption' | null => {
  if (security.secGroup === SEC_GROUP_FX) {
    return 'fxForward';
  } else if (security.secGroup === SEC_GROUP_OPTION && security.secType === SEC_TYPE_CUROTC) {
    return 'fxOption';
  }
  return null;
};

export const calculateNotionalMarketValueFromContractNotional = (
  contractNotional: number,
  security: Security,
  portfolioCurrency: string,
  fxRates: FXRates
) => {
  const receiveLegMultiplier = security.analytics?.receiveLegMultiplier ?? security.valuations?.receiveLegMultiplier;
  if (security.secGroup === SEC_GROUP_FX && !isNil(receiveLegMultiplier) && security.derivative) {
    return (
      contractNotional *
      receiveLegMultiplier *
      getFxConversionRate(security.derivative.contractCurrency, portfolioCurrency, fxRates)
    );
  } else if (security.secGroup === SEC_GROUP_OPTION && security.secType === SEC_TYPE_CUROTC && security.derivative) {
    // Use security.price if available, otherwise fall back to notionalMultiplier or 1
    const priceMultiplier =
      typeof security.price === 'number' ? security.price / 100 : (security.valuations?.notionalMultiplier ?? 1);
    return (
      contractNotional *
      priceMultiplier *
      getFxConversionRate(security.derivative.contractCurrency, portfolioCurrency, fxRates)
    );
  }
  throw new Error('Not supported for this type of security');
};

export const calculateContractNotionalFromNotionalMarketValue = (
  notionalMarketValue: number,
  security: Security,
  portfolioCurrency: string,
  fxRates: FXRates
) => {
  const receiveLegMultiplier = security.analytics?.receiveLegMultiplier ?? security.valuations?.receiveLegMultiplier;
  if (security.secGroup === SEC_GROUP_FX && !isNil(receiveLegMultiplier) && security.derivative) {
    return (
      (notionalMarketValue / receiveLegMultiplier) *
      getFxConversionRate(portfolioCurrency, security.derivative.contractCurrency, fxRates)
    );
  } else if (security.secGroup === SEC_GROUP_OPTION && security.secType === SEC_TYPE_CUROTC && security.derivative) {
    // Use security.price if available, otherwise fall back to notionalMultiplier or 1
    const priceMultiplier =
      typeof security.price === 'number' ? security.price / 100 : (security.valuations?.notionalMultiplier ?? 1);
    const contractNotional =
      (notionalMarketValue / priceMultiplier) *
      getFxConversionRate(portfolioCurrency, security.derivative.contractCurrency, fxRates);
    return security.derivative.putCall === 'P' ? -1 * contractNotional : contractNotional;
  }
  throw new Error('Not supported for this type of security');
};

export const calculateNetNotionalValuesFromNotionalMv = (
  notionalMV: number,
  security: Security,
  baseCurrency: string,
  fxRates: FXRates
) => {
  if (!security.derivative) {
    throw Error('Security is missing derivative data');
  }
  let {
    derivative: { contractCurrency, alternativeCurrency },
  } = security;
  const leg1NetNotional = calculateContractNotionalFromNotionalMarketValue(notionalMV, security, baseCurrency, fxRates);
  const leg1 = {
    currency: contractCurrency,
    netNotional: calculateContractNotionalFromNotionalMarketValue(notionalMV, security, baseCurrency, fxRates),
    baseNetNotional: leg1NetNotional * getFxConversionRate(contractCurrency, baseCurrency, fxRates),
    netNotionalPct: null,
  };
  const leg2NetNotional = calculateAlternateNotionalFromContractNotional(leg1.netNotional, security);
  const leg2 = {
    currency: alternativeCurrency,
    netNotional: leg2NetNotional,
    baseNetNotional: leg2NetNotional * getFxConversionRate(alternativeCurrency, baseCurrency, fxRates),
    netNotionalPct: null,
  };
  return [leg1, leg2];
};

export const getCurrencyConvertedValue = (
  originalValue: number,
  originalCurrency: string,
  newCurrency: string,
  decimalPlaces: number,
  fxRates: FXRates
) => {
  const conversionRate = getFxConversionRate(originalCurrency, newCurrency, fxRates);
  const convertedValue = originalValue * conversionRate;
  const multiplesOfTen = Math.pow(10, decimalPlaces);
  return Math.round(convertedValue * multiplesOfTen) / multiplesOfTen;
};

/**
 * Converts a value between currencies using decimal.js for precision.
 *
 * Unlike getCurrencyConvertedValue, this function:
 * - Uses decimal.js internally to avoid floating-point errors
 * - Applies cleanFloatingPointNoise guard to detect and remove IEEE-754 artifacts
 * - Returns a number (for backward compatibility with existing code)
 *
 * @param originalValue - The value to convert
 * @param originalCurrency - The source currency code
 * @param newCurrency - The target currency code
 * @param fxRates - FX rates object
 * @returns The converted value as a number with IEEE-754 noise removed
 */
export const getCurrencyConvertedValueWithDecimal = (
  originalValue: number,
  originalCurrency: string,
  newCurrency: string,
  fxRates: FXRates
): number => {
  if (originalCurrency === newCurrency) {
    return originalValue;
  }
  const conversionRate = getFxConversionRate(originalCurrency, newCurrency, fxRates);
  const rawResult = new Decimal(originalValue).mul(conversionRate);
  const cleanedString = cleanFloatingPointNoise(rawResult.toString());
  return Number(cleanedString);
};
